import React, { useRef, useCallback } from "react";
import Dropzone from "react-dropzone";
import { withTranslation } from "react-i18next";
import { Box, Grid, Slide, TableRow } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { alpha } from "@mui/material";
import { ProbingDropzone } from "@knide/fs-prober/react";

// TODO: DropzoneProvider로 rename하고 version 체크 대상은 props로 받아와서 처리.

const UploadDropzoneProvider = (WrappedComponent) => {
    const { children, ...rest } = WrappedComponent;
    const {
        accept,
        className,
        onDrop,
        disableClick,
        disabled,
        target,
        t,
        stage,
        extendPath,
        variant,
        createNewFolders,
        parentFolder,
    } = rest;
    const thisDiv = useRef();

    const allFoldersRef = useRef([]); // 드래그된 모든 폴더 목록
    const createdFoldersRef = useRef(new Set()); // 이미 생성된 폴더 경로 추적 (중복 생성 방지)

    const handleProbingDrop = ({ hierarchyDetails }) => {
        if (!hierarchyDetails.allFolders || hierarchyDetails.allFolders.length === 0) {
            return;
        }
        allFoldersRef.current = hierarchyDetails.allFolders;
    };

    const handleCreateNewFolders = useCallback(
        async (needRefresh) => {
            if (!allFoldersRef.current || allFoldersRef.current.length === 0) {
                return;
            }
            const createFolderKey = (folderPath) => {
                const rootPath = extendPath ? extendPath : `${parentFolder?.id || target?.id || "root"}`;
                return `${rootPath}_${folderPath}`;
            };
            // 이미 생성된 폴더를 제외한 새로운 폴더만 필터링
            const foldersToCreate = allFoldersRef.current.filter((folder) => {
                const folderPath = folder.path;
                const folderKey = createFolderKey(folderPath);
                return !createdFoldersRef.current.has(folderKey);
            });

            if (foldersToCreate.length === 0) {
                allFoldersRef.current = [];
                return;
            }

            try {
                await createNewFolders(foldersToCreate, target?.prefix, extendPath, needRefresh);

                // 생성 성공한 폴더들을 생성 완료 목록에 추가
                foldersToCreate.forEach((folder) => {
                    const folderPath = folder.path;
                    const folderKey = createFolderKey(folderPath);
                    createdFoldersRef.current.add(folderKey);
                });
            } catch (error) {
                console.error("Error creating new folders:", error);
            } finally {
                allFoldersRef.current = [];
            }
        },
        [createNewFolders, extendPath, parentFolder],
    );

    const handleOnDrop = useCallback(
        (acceptedFiles, rejectedFiles, event) => {
            const needUpload = acceptedFiles.length > 0;
            const needRefresh = !needUpload;
            if (needUpload) {
                onDrop(acceptedFiles, null, extendPath || target);
            }
            if (createNewFolders) handleCreateNewFolders(needRefresh);

            event.stopPropagation();
            event.preventDefault();
        },
        [onDrop, extendPath, target, handleCreateNewFolders],
    );

    const getFoldersInnerData = (folderEntry) => {
        return new Promise((resolve, reject) => {
            let directoryReader = folderEntry.createReader();
            directoryReader.readEntries(function (entries) {
                resolve(entries);
            });
        });
    };

    const getAllFileEntry = async (dataTransferItems, skipSetEntry) => {
        let fileList = [];
        if (dataTransferItems.length === 0) {
            return [];
        }
        const promises = Object.entries(dataTransferItems).map(async ([key, item]) => {
            let _item = skipSetEntry ? item : item.webkitGetAsEntry();
            if (_item && _item.isFile === true) {
                fileList.push(_item);
            }
            if (_item && _item.isDirectory === true) {
                fileList.push(_item);
                let innerData = await getFoldersInnerData(_item);
                innerData = await getAllFileEntry(innerData, true);
                // console.log('innerData', innerData)
                innerData.length > 0 && fileList.push(...innerData);
            }
        });
        await Promise.all(promises);
        return fileList;
    };

    const convertEntryToFile = async (fileEntry) => {
        // console.log('fileEntry', fileEntry)
        return new Promise((resolve, reject) => {
            const success = (results) => {
                results.fullPath = fileEntry.fullPath;
                results.isFolder = fileEntry.isDirectory;
                resolve(results);
            };
            const error = (error) => {
                reject(error);
            };
            if (fileEntry.isFile) {
                fileEntry.file(success, error);
            } else {
                const createFolder = new File([], fileEntry.name, {});
                createFolder.fullPath = fileEntry.fullPath;
                createFolder.isFolder = fileEntry.isDirectory;
                resolve(createFolder);
            }
        });
    };

    return disabled ? (
        <Dropzone
            accept={accept}
            onDrop={() => {
                console.log("blocked");
            }}
            disableClick={disableClick}
        >
            {({ isDragActive }) => {
                return (
                    <React.Fragment>
                        <TableRow className={`dropzone ${isDragActive ? "active-disabled" : ""}`}>{children}</TableRow>
                        {isDragActive && (
                            <div className={"drag-wrap"}>
                                <div className={"drag-active"}>
                                    <i className={"sprite sprite-cloud-upload-impossible"}></i>
                                    <p className="text-danger">{`${t(
                                        `common::msg::You cannot upload files to this list.`,
                                        `You cannot upload files to this list.`,
                                    )}`}</p>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            }}
        </Dropzone>
    ) : (
        <ProbingDropzone
            accept={accept}
            onDrop={handleOnDrop}
            onProbingDrop={handleProbingDrop}
            noClick={true}
            disableClick={disableClick}
            disabled={disabled}
        >
            {(props) => {
                const { getRootProps, getInputProps, isDragActive, rootRef } = props;
                const findOne =
                    rootRef.current && rootRef.current.parentNode.parentNode.parentNode.parentNode.parentNode;
                if (findOne && findOne.classList.contains("dropzone-wrapper")) {
                    const findAll = document.querySelectorAll(".dropzone.active");
                    if (isDragActive) {
                        findOne.classList.add("mother");
                    } else {
                        if (findAll.length > 2) {
                        } else {
                            findOne.classList.remove("mother");
                        }
                    }
                }

                return variant === "table" ? (
                    <TableRow
                        ref={thisDiv}
                        {...getRootProps({
                            className: `dropzone ${isDragActive ? "active" : ""} ${
                                extendPath ? "folder-dropzone" : ""
                            }`,
                        })}
                        sx={{
                            position: `relative`,
                            zIndex: 1,
                            "&:after": {
                                content: isDragActive ? "''" : `none`,
                                position: `absolute`,
                                left: 8,
                                top: 8,
                                right: 8,
                                bottom: 8,
                                backgroundColor: alpha(theme.palette.primary[100], 0.4),
                                border: `1px dashed ${theme.palette.secondary[400]}`,
                                borderRadius: 2,
                            },
                        }}
                    >
                        {children}
                    </TableRow>
                ) : (
                    <Box
                        className={className}
                        sx={{
                            display: `flex`,
                            flex: 1,
                            maxWidth: `100%`,
                            maxHeight: `100%`,
                            overflow: `hidden`,
                            flexDirection: `column`,
                            "&.mother": {
                                "> .dropzone": {
                                    "&.active": {
                                        "&:after": {
                                            content: `none`,
                                        },
                                        ".folder-dropzone": {
                                            opacity: 1,
                                        },
                                    },
                                },
                            },
                            "> .dropzone": {
                                "&.active": {
                                    ".folder-dropzone": {
                                        zIndex: 4,
                                        opacity: 0.5,
                                    },
                                },
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: `relative`,
                                width: `100%`,
                                maxHeight: `100%`,
                                overflow: `hidden`,
                                "&:after": {
                                    content: isDragActive ? "''" : `none`,
                                    position: `absolute`,
                                    left: 8,
                                    top: 8,
                                    right: 8,
                                    bottom: 8,
                                    backgroundColor: alpha(theme.palette.primary[100], 0.4),
                                    border: `1px dashed ${theme.palette.secondary[400]}`,
                                    borderRadius: 2,
                                    zIndex: 3,
                                },
                            }}
                            className={`dropzone ${isDragActive ? "active" : ""} ${
                                extendPath ? "folder-dropzone" : ""
                            }`}
                            ref={thisDiv}
                            {...getRootProps({
                                className: `dropzone ${isDragActive ? "active" : ""} ${
                                    extendPath ? "folder-dropzone" : ""
                                }`,
                            })}
                        >
                            {children}

                            <Slide in={isDragActive} direction="up" container={thisDiv.current}>
                                <Grid
                                    container
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                    gap={0.75}
                                    flexDirection={"column"}
                                    sx={{
                                        position: `absolute`,
                                        left: `50%`,
                                        bottom: 20,
                                        zIndex: 9999,
                                        width: 370,
                                        height: 122,
                                        marginLeft: `-185px`,
                                        borderRadius: 1,
                                        backgroundColor: theme.palette.secondary.main,
                                        color: theme.palette.secondary.contrastText,
                                    }}
                                >
                                    <i className={"sprite sprite-cloud-upload-primary"}></i>
                                    <p>{t(`common::msg::Drag your files to upload`, `Drag your files to upload`)}</p>
                                </Grid>
                            </Slide>
                        </Box>
                    </Box>
                );
            }}
        </ProbingDropzone>
    );
};

export default React.memo(withTranslation()(UploadDropzoneProvider));
